import { useCallback, useEffect, useMemo, useState } from "react"
import {
  useGetAccountByAddress,
  useGetCurrentAccount,
  useGetSession,
  useLinkAccount,
  useSignInWithWallet,
  useSignUpWithWallet,
  useUpdateMezoId,
} from "@mezo-org/passport"
import { PayloadAction } from "@reduxjs/toolkit"
import { useNavigate } from "@tanstack/react-router"
import { useAccount, useBalance } from "wagmi"

import {
  checkAvaiableUsername,
  grantMatsForSignUp,
  selectOnboardingStep,
  selectUserAuthId,
  selectUsername,
  selectWalletAddress,
  selectWalletBalances,
  setOnboardingStep,
  setOpenModal,
  setUsername,
  useDappDispatch,
  useDappSelector,
} from "#/redux-state"
import {
  ENABLE_MATS_REWARDS_FOR_SIGNUP,
  FRIENDLY_DECIMALS_AMOUNT,
  routes,
} from "#/shared/constants"
import {
  ExtendedConector,
  OnboardingStep,
  WindowPopupStatus,
} from "#/shared/types"
import {
  bigIntToFloat,
  getConnectorIcon,
  isEVMAddress,
  normalizeAddress,
  validateUsername,
} from "#/shared/utils"

import { useLoginAndFetchAccount } from "./account"
import { usePopupWindow } from "./helpers"
import { useFetchMats } from "./mats"
import {
  usePosthogSignUpEvent,
  usePosthogUsernameSetEvent,
  usePosthogWalletConnectedEvent,
} from "./posthog-events"
import { useDAppRoute, useLandingRoute } from "./routes"
import {
  useSegmentSignUpEvent,
  useSegmentUsernameSetEvent,
  useSegmentWalletConnectedEvent,
} from "./segment-events"
import { useFetchWalletBalances } from "./wallet"

export const useGetLinkedAccountAddresses = () => {
  const { refetch: getAccount } = useGetCurrentAccount({
    enabled: false,
  })

  const isAddressLinkedToAccount = useCallback(
    async (address: string) => {
      const { data: currentAccount } = await getAccount()

      if (!currentAccount || !currentAccount.linkedAccounts) return false

      // We map through linked addresses to find all addresses (BTC and EVM)
      // that are connected to authenticated user
      const linkedAddresses = currentAccount.linkedAccounts
        .map((item) =>
          item.type === "wallet"
            ? normalizeAddress(item.btcAddress ?? item.evmAddress)
            : null,
        )
        .filter((item) => item !== null)

      return linkedAddresses.includes(normalizeAddress(address))
    },
    [getAccount],
  )

  return { isAddressLinkedToAccount }
}

export const useConnectWalletMezoAccount = () => {
  const walletAddress = useDappSelector(selectWalletAddress) ?? ""
  const onboardingStep = useDappSelector(selectOnboardingStep)

  const isDappRoute = useDAppRoute()
  const { isLandingRoute } = useLandingRoute()

  const navigate = useNavigate()
  const dispatch = useDappDispatch()

  const { signInWithWalletAsync } = useSignInWithWallet()
  const { emitWalletConnectedSegmentEvent } = useSegmentWalletConnectedEvent()
  const { emitWalletConnectedEvent } = usePosthogWalletConnectedEvent()
  const { isAddressLinkedToAccount } = useGetLinkedAccountAddresses()

  const { refetch: getSession } = useGetSession(undefined, { enabled: false })

  const { refetch: getAccountAddress } = useGetAccountByAddress(walletAddress, {
    enabled: false,
  })

  const { loginAndFetchAccount } = useLoginAndFetchAccount()

  const signInWithWalletHandler = useCallback(
    async (address: string) => {
      if (!isDappRoute) return false

      // Initially the address will be undefined so we need to use "refetch"
      // to get correct data
      const { data: session } = await getSession()
      const { data: accountAddress } = await getAccountAddress()

      // We are checking if account exists. If not we don't sign in user and we
      // proceed to sign up flow
      if (accountAddress) {
        // Creating session from Passport API if not available.
        // Additionally we check if current account matched the connecting address.
        // If it does we don't need to create session as it is already established.
        const isLinked = await isAddressLinkedToAccount(address)

        if (!isLinked && session && "nonce" in session) {
          await signInWithWalletAsync()

          // Redirect user to the app if user is on
          // landing page
          if (isLandingRoute) {
            navigate({ to: routes.OVERVIEW })
          }
        }
      } else {
        if (onboardingStep <= OnboardingStep.ACCOUNT_NOT_FOUND) {
          // If account is not found we proceed to account not found screen
          dispatch(setOpenModal("onboarding"))
          dispatch(setOnboardingStep(OnboardingStep.ACCOUNT_NOT_FOUND))
        }

        return false
      }

      // Fetching account data when session has been already established
      const account = await loginAndFetchAccount()

      // Account has not been found in the database
      if (!account) return false

      // If user signs in successfully we emit a Posthog event
      emitWalletConnectedEvent(account.authId, address, account.username)
      emitWalletConnectedSegmentEvent(account.authId, address, account.username)

      return true
    },
    [
      onboardingStep,
      isDappRoute,
      isLandingRoute,
      navigate,
      signInWithWalletAsync,
      dispatch,
      emitWalletConnectedEvent,
      emitWalletConnectedSegmentEvent,
      getSession,
      isAddressLinkedToAccount,
      getAccountAddress,
      loginAndFetchAccount,
    ],
  )

  return { signInWithWallet: signInWithWalletHandler }
}

export const useCreateAccountModal = () => {
  const [isInputActive, setIsInputActive] = useState(false)
  const [isUsernameValid, setIsUsernameValid] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [usernameTaken, setUsernameTaken] = useState(false)

  const username = useDappSelector(selectUsername)
  const dispatch = useDappDispatch()

  const updateUsernameInput = useCallback(
    async (value: string) => {
      dispatch(setUsername(value))
      setUsernameTaken(false)
      setIsUsernameValid(validateUsername(value))
    },
    [dispatch],
  )

  const onBlur = useCallback(
    async (value: string) => {
      if (!value) return

      // Pretend as input is active when checking for username availability
      setIsInputActive(true)

      // Check if this username already exists in database
      const { payload: usernameAvailable } = (await dispatch(
        checkAvaiableUsername(value),
      )) as PayloadAction<boolean>

      // Username is valid if it matches the username requirements
      // and does not exist in database
      setUsernameTaken(!usernameAvailable)
      setIsUsernameValid(usernameAvailable && validateUsername(value))
      setIsInputActive(false)
    },
    [dispatch],
  )

  const showInvalidUsernameInfo = useMemo(
    () => !isUsernameValid && username !== "",
    [isUsernameValid, username],
  )

  const isCreateAccountButtonDisabled = useMemo(
    () =>
      showInvalidUsernameInfo || isInputActive || !username || !termsAccepted,
    [showInvalidUsernameInfo, username, termsAccepted, isInputActive],
  )

  return {
    username,
    showInvalidUsernameInfo,
    usernameTaken,
    updateUsernameInput,
    termsAccepted,
    setTermsAccepted,
    setIsInputActive,
    isCreateAccountButtonDisabled,
    onBlur,
  }
}

export const useCreateWalletAccount = () => {
  const username = useDappSelector(selectUsername)
  const userAuthId = useDappSelector(selectUserAuthId)
  const dispatch = useDappDispatch()

  const { updateMezoIdAsync } = useUpdateMezoId()
  const { signUpWithWalletAsync } = useSignUpWithWallet()
  const { emitUsernameSetSegmentEvent } = useSegmentUsernameSetEvent()
  const { emitSignUpRewardClaimEvent } = usePosthogSignUpEvent()
  const { emitUsernameSetEvent } = usePosthogUsernameSetEvent()
  const { refetch: fetchMats } = useFetchMats()

  const createWalletAccountHandler = useCallback(async () => {
    // When account creation has started we are showing user loading screen
    // for account creation
    dispatch(setOnboardingStep(OnboardingStep.ACCOUNT_CREATION_IN_PROGRESS))

    // Running sign up with wallet to create account in database
    await signUpWithWalletAsync()

    // Updating user's Mezo ID. It must include .mezo at the end
    await updateMezoIdAsync(`${username}.mezo`)

    // Emit event when Mezo ID is updated
    if (userAuthId) {
      emitUsernameSetEvent(userAuthId, username)
      emitUsernameSetSegmentEvent(userAuthId, username)
    }

    if (ENABLE_MATS_REWARDS_FOR_SIGNUP) {
      // Claim rewards for sign up
      dispatch(setOnboardingStep(OnboardingStep.CLAIMING_REWARDS_IN_PROGRESS))
      const { payload: matsGranted } = await dispatch(grantMatsForSignUp())

      // Emit Posthog event when mats granted for sign up
      if (matsGranted) {
        emitSignUpRewardClaimEvent()
        await fetchMats()
      }
    }

    // Proceed to account created screen and show slide up for discord connection
    dispatch(setOnboardingStep(OnboardingStep.ACCOUNT_CREATED))
  }, [
    dispatch,
    signUpWithWalletAsync,
    updateMezoIdAsync,
    username,
    userAuthId,
    emitUsernameSetEvent,
    emitUsernameSetSegmentEvent,
    emitSignUpRewardClaimEvent,
    fetchMats,
  ])

  return { createWalletAccount: createWalletAccountHandler }
}

export const useOnboardingModalWalletIcon = () => {
  const [walletIcon, setWalletIcon] = useState<string | null>(null)
  const { connector } = useAccount()

  useEffect(() => {
    const resolveConnectorIcon = async () => {
      const rawConnectorIcon = getConnectorIcon(connector as ExtendedConector)

      if (typeof rawConnectorIcon === "string") {
        setWalletIcon(rawConnectorIcon)
        return
      }

      const connectorIconPromise = await rawConnectorIcon()
      setWalletIcon(connectorIconPromise)
    }

    resolveConnectorIcon()
  }, [connector])

  return { walletIcon }
}

export const useOnboardingWallet = () => {
  const walletAddress = useDappSelector(selectWalletAddress)
  const { BTC } = useDappSelector(selectWalletBalances)

  const { walletIcon } = useOnboardingModalWalletIcon()
  const { connector } = useAccount()

  const { data: ethBalance } = useBalance({
    address: walletAddress! as `0x${string}`,
  })

  const { fetchWalletBalances } = useFetchWalletBalances()

  useEffect(() => {
    const fetchBalances = async () => {
      if (!connector) return

      await fetchWalletBalances({ fetchOnChainData: true, refetch: true })
    }

    fetchBalances()
  }, [fetchWalletBalances, connector])

  const walletBalance = useMemo(() => {
    if (!walletAddress) return ""

    if (isEVMAddress(walletAddress)) {
      return `${bigIntToFloat(
        ethBalance ? ethBalance.value : 0n,
        ethBalance ? ethBalance.decimals : 18,
        FRIENDLY_DECIMALS_AMOUNT,
      )} ${ethBalance ? ethBalance.symbol : ""}`
    }

    return `${bigIntToFloat(
      BTC.balance,
      Number(BTC.decimals),
      FRIENDLY_DECIMALS_AMOUNT,
    )} BTC`
  }, [BTC, ethBalance, walletAddress])

  return {
    walletIcon,
    walletName: connector?.name ?? "",
    walletBalance,
  }
}

export const useLinkDiscordToWalletAccount = (onClose: () => void) => {
  const { linkAccountAsync } = useLinkAccount()

  const { popupStatus, popupPayload, popupError, openPopupWindow } =
    usePopupWindow("code")

  const { refetch: getSession } = useGetSession(popupPayload, {
    enabled: false,
  })

  const { signInWithWalletAsync } = useSignInWithWallet()

  const { data: currentAccount, refetch: getAccount } = useGetCurrentAccount({
    enabled: false,
  })

  const { emitSignUpDiscordAddedSegmentEvent } = useSegmentSignUpEvent()
  const { emitSignUpDiscordAddedEvent } = usePosthogSignUpEvent()

  const linkDiscordHandler = useCallback(async () => {
    // FIXME: This not ideal solution, but when user rejected discord authorization
    // the session is abandoned. We need to see if it is possible to negate
    // this behavior on Supabase side. Not being an ideal solution, right now
    // when user reject linking we need to ask for additional wallet signature
    // the establish a new session
    if (popupError) {
      await signInWithWalletAsync()
    }

    const linkAccountResult = await linkAccountAsync({ type: "discord" })

    if ("redirectTo" in linkAccountResult) {
      // Open popup window for Discord authorization
      openPopupWindow(linkAccountResult.redirectTo)
    }
  }, [linkAccountAsync, openPopupWindow, popupError, signInWithWalletAsync])

  useEffect(() => {
    const fetchLinkedAccounts = async () => {
      if (popupError || popupStatus !== WindowPopupStatus.CLOSED) return

      // Close slide up when authorization has been completed and
      // no authorization error is present
      onClose()

      // Prevent creating new session if we already have an account
      if (currentAccount) {
        const linkedDiscordAccount = currentAccount.linkedAccounts?.find(
          (linkedAccount) => linkedAccount.type === "discord",
        )

        // Emit event when discord is linked to account after signup
        if (
          linkedDiscordAccount &&
          currentAccount.mezoId &&
          "name" in linkedDiscordAccount
        ) {
          emitSignUpDiscordAddedEvent(
            currentAccount.mezoId,
            linkedDiscordAccount.name,
          )
          emitSignUpDiscordAddedSegmentEvent(
            currentAccount.mezoId,
            linkedDiscordAccount.name,
          )
        }

        return
      }

      // Create new session after discord authorization has been completed
      // and refetch account data
      await getSession()
      await getAccount()
    }

    fetchLinkedAccounts()
  }, [
    onClose,
    popupStatus,
    getSession,
    getAccount,
    currentAccount,
    popupError,
    emitSignUpDiscordAddedEvent,
    emitSignUpDiscordAddedSegmentEvent,
  ])

  return {
    linkDiscord: linkDiscordHandler,
    linkingStatus: popupStatus,
    authorizationError: popupError,
  }
}
